import { Button } from 'component/Button'
import { Gap, Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Upload } from 'component/Upload'
import { Vertical } from 'component/Vertical'
import { i18n } from 'i18n'
import { observer } from 'mobx-react-lite'
import { DashedButton } from 'page/ProfileScreen/basic/DashedButton'
import { SelfieHelpStep } from 'page/ProfileScreen/HelpStep/SelfieHelpStep'
import ErrorSvg from 'page/SelfieScreen/asset/error.svg'
import PersonSvg from 'page/SelfieScreen/asset/person.svg'
import SuccessSvg from 'page/SelfieScreen/asset/success.svg'
import { useQrUpload } from 'page/SelfieScreen/useQrUpload'
import { ReactElement } from 'react'
import { store } from 'store/store'
import css from './SelfieScreen.module.scss'

type Props = {
  children: [ReactElement, ReactElement]
}

const Base = observer(({ children }: Props) => {
  const [center, bottom] = children
  return <Vertical className={css.scroll} center>
    <Vertical className={css.inner} gap={48} pad={24} center opposite>
      <Space />
      <Space wide flex="1 0 auto">
        {center}
      </Space>
      {bottom}
    </Vertical>
    {store.profile.step === 'help' && <SelfieHelpStep />}
  </Vertical>
})

export const SelfieScreen = observer(() => {
  const { state, input, reset, onClose, onHelp, onFile, onCamera, onCameraFile } = useQrUpload('selfie')

  if (state === 'processing') return <Base>
    <Vertical gap={48} wide center>
      <Typo primary>
        <PersonSvg width={240} height={240} />
      </Typo>
      <Vertical gap={16} wide>
        <Space className={css.line}>
          <Space className={css.bar} />
        </Space>
        <Typo size={16} center pre>
          {i18n('UploadingPhoto')}
        </Typo>
      </Vertical>
    </Vertical>
    <Button height={48} upper large flat wide onClick={reset}>
      {i18n('Cancel')}
    </Button>
  </Base>

  if (state === 'success') return <Base>
    <Vertical gap={48} wide center>
      <SuccessSvg width={100} height={100} />
      <Typo size={16} center pre balance>
        {i18n('PhotoSuccessfullyUploadedContinueTryingOnStand')}
      </Typo>
    </Vertical>
    <Button height={48} upper large flat wide onClick={onClose}>
      {i18n('Close')}
    </Button>
  </Base>

  if (state === 'error') return <Base>
    <Vertical gap={48} wide center>
      <ErrorSvg width={100} height={100} />
      <Typo size={16} center pre>
        {i18n('SomethingWentWrongPleaseTryAgain')}
      </Typo>
    </Vertical>
    <Button height={48} upper large flat wide onClick={reset}>
      {i18n('GoBack')}
    </Button>
  </Base>

  return <Base>
    <Vertical wide>
      <Vertical center>
        <Typo primary>
          <PersonSvg width={240} height={240} />
        </Typo>
      </Vertical>
      <Gap height={16} />
      <Space wide gap={12} opposite wrap>
        <Typo size={13} medium upper>
          {i18n('Selfie')}
        </Typo>
        <DashedButton onClick={onHelp}>
          {i18n('HowToTakeSelfie')}
        </DashedButton>
      </Space>
      <Gap height={12} />
      <Typo size={13} pre center>
        {i18n('TakeSelfieOrChoosePhotoAndItWillAppearOnStand')}
      </Typo>
    </Vertical>
    <Vertical gap={12} wide>
      <Space>
        <Button height={48} upper large primary wide onClick={onCamera}>
          {i18n('OpenCamera')}
        </Button>
      </Space>
      <Upload wide accept="image" onFile={onFile}>
        <Button height={48} upper large flat wide>
          {i18n('LoadFromStorage')}
        </Button>
      </Upload>
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="user"
        onChange={onCameraFile}
        style={{ display: 'none' }}
      />
    </Vertical>
  </Base>
})
